import { useEffect, useState } from 'react';
import type { Rrpp } from '../../../api/ops/listas';
import type { Liquidacion } from '../../../api/ops/portalRrpp';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Paginacion } from '../../../componentes/Paginacion';
import { Tabla, type FilaTabla } from '../../../componentes/Tabla';
import { dinero } from '../../../logica/dinero';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { AvisoVista, Vacia } from '../comun';
import { iniciales, useCarga } from '../uso';
import { Cargando, EstadoDelError, SinPermiso } from '../estados';
import { AYUDA, chipLiquidacion, diaHora, nombreRrpp, opsDe, periodo, PERMISOS, quedaPorPagar } from '../textos';
import { FichaLiquidacion } from './FichaLiquidacion';
import { Preparar } from './Preparar';

/**
 * Apartado Liquidaciones (10.11): Por liquidar (RRPP con comisiones pendientes y sin borrador) · Borradores · Cerradas ·
 * Descartadas, con «Anterior / Siguiente» (G121). Preparar (`createSettlement`), ficha con ajustes, cerrar, descartar y
 * pagos. Solo Propietario y Administrador (G8); quien no tiene el apartado no lo ve (G120). `?preparar=<rrpp>` abre
 * «Preparar liquidación» con ese RRPP (desde su ficha).
 */
const VISTAS = [
    { clave: 'pendiente', etiqueta: 'Por liquidar' },
    { clave: 'draft', etiqueta: 'Borradores' },
    { clave: 'closed', etiqueta: 'Cerradas' },
    { clave: 'discarded', etiqueta: 'Descartadas' },
] as const;

export type PanelLiquidacion = { tipo: 'preparar'; rrpp: string } | { tipo: 'ficha'; id: string };

export interface ContextoLiquidaciones {
    props: PropsPantalla;
    rrpp: ReadonlyMap<string, Rrpp>;
    borradores: readonly Liquidacion[];
    abrir: (p: PanelLiquidacion | null) => void;
    recargar: () => void;
}

export function ApartadoLiquidaciones(props: PropsPantalla) {
    const { negocio, movil, soloLectura, busqueda } = props;
    const ops = opsDe(props.ops);
    const permisos = usePermisos();
    const ver = permisos.puede(PERMISOS.liquidacionesVer, 'all');
    const gestionar = permisos.puede(PERMISOS.liquidacionesGestionar) && !soloLectura;
    const [vista, setVista] = useState<(typeof VISTAS)[number]['clave']>('pendiente');
    const [panel, setPanel] = useState<PanelLiquidacion | null>(() => {
        const p = busqueda.get('preparar');
        return p === null ? null : { tipo: 'preparar', rrpp: p };
    });
    const [vuelta, setVuelta] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    useEffect(() => {
        reiniciar();
    }, [vista, reiniciar]);
    const rrpp = useCarga(ver ? () => ops.listPromoters(negocio, { limit: 100 }) : null, [negocio, vuelta]);
    const borradores = useCarga(ver ? () => ops.listSettlements(negocio, { status: 'draft', limit: 100 }) : null, [negocio, vuelta]);
    const pagina = useCarga(ver && vista !== 'pendiente' ? () => ops.listSettlements(negocio, { status: vista, cursor: pag.cursor, limit: 25 }) : null, [negocio, vista, pag.cursor, vuelta]);
    const mapa = new Map((rrpp.datos?.data ?? []).map((p) => [p.id, p]));
    const ctx: ContextoLiquidaciones = { props, rrpp: mapa, borradores: borradores.datos?.data ?? [], abrir: setPanel, recargar: () => { setVuelta((v) => v + 1); } };

    const acciones: AccionCabecera[] = [];
    if (ver && gestionar) {
        acciones.push({ clave: 'preparar', texto: 'Preparar liquidación', icono: 'wallet', onClick: () => { setPanel({ tipo: 'preparar', rrpp: '' }); } });
    }
    useCabecera({ ayuda: ver ? (AYUDA.liquidaciones ?? []) : [], acciones });

    if (!ver) {
        return <SinPermiso props={props} seccion="Liquidaciones" />;
    }
    if (rrpp.error !== null && rrpp.datos === null) {
        return <EstadoDelError props={props} error={rrpp.error} seccion="Liquidaciones" nombre="las liquidaciones" onReintentar={rrpp.recargar} />;
    }
    if (rrpp.datos === null || borradores.datos === null) {
        return <Cargando nombre="las liquidaciones" />;
    }
    const conBorrador = new Set(ctx.borradores.map((b) => b.promoter_id));
    const porLiquidar = rrpp.datos.data.filter((p) => (p.pending_total?.amount ?? 0) > 0 && !conBorrador.has(p.id));
    const indice = VISTAS.findIndex((v) => v.clave === vista);

    return (
        <div className="rv-ls-apartado">
            <div className="rv-ls-cifras-tres">
                <button type="button" className="rv-ls-cifra rv-ls-cifra--oscura" onClick={() => { setVista('pendiente'); }}>
                    <strong>Por liquidar</strong>
                    <span><b>{porLiquidar.length}</b> RRPP</span>
                    <small>con comisiones pendientes</small>
                </button>
                <button type="button" className="rv-ls-cifra" onClick={() => { setVista('draft'); }}>
                    <strong>En borrador</strong>
                    <span><b>{ctx.borradores.length}</b> {ctx.borradores.length === 1 ? 'liquidación' : 'liquidaciones'}</span>
                    <small>{ctx.borradores.length > 0 ? 'sin cerrar todavía' : 'Ninguna sin cerrar'}</small>
                </button>
            </div>
            <div className="rv-ls-vistas">
                <Filtros etiqueta="Vista" items={VISTAS.map((v) => ({ etiqueta: v.etiqueta }))} valor={indice} onCambio={(i) => { setVista(VISTAS[i]?.clave ?? 'pendiente'); }} />
            </div>
            {vista === 'pendiente' ? (
                porLiquidar.length === 0 ? (
                    <Vacia icono="wallet" titulo="No hay nada por liquidar." texto="Cuando termine una noche, las comisiones de tus RRPP aparecerán aquí listas para liquidar." />
                ) : (
                    <Tabla
                        etiqueta="RRPP por liquidar"
                        movil={movil}
                        columnas={[
                            { clave: 'rrpp', titulo: 'RRPP', ancho: 'minmax(220px, 1.6fr)' },
                            { clave: 'pendiente', titulo: 'Pendiente', ancho: 'minmax(120px, 0.8fr)', alinear: 'derecha' },
                        ]}
                        filas={porLiquidar.map((p) => ({
                            clave: p.id,
                            etiqueta: nombreRrpp(p),
                            onAbrir: gestionar ? () => { setPanel({ tipo: 'preparar', rrpp: p.id }); } : undefined,
                            celdas: {
                                rrpp: (
                                    <span className="rv-ls-celda-persona">
                                        <span className={`rv-ca-inicial${p.status === 'active' ? ' rv-ca-inicial--roja' : ''}`} aria-hidden="true">{iniciales(nombreRrpp(p))}</span>
                                        <span className="rv-ca-fila__texto">
                                            <strong>{nombreRrpp(p)}{p.status !== 'active' && <Chip estado="neutral">Desactivado</Chip>}</strong>
                                            <span>{p.status === 'active' ? `Código ${p.sales_code}` : 'Desactivado: se le liquida lo pendiente'}</span>
                                        </span>
                                    </span>
                                ),
                                pendiente: <span className="rv-ls-importe"><strong>{dinero(p.pending_total)}</strong><small>pendiente</small></span>,
                            },
                            tarjeta: <span className="rv-ca-fila__texto"><strong>{nombreRrpp(p)}</strong><span>{dinero(p.pending_total)} pendiente</span></span>,
                            acciones: gestionar ? (
                                <button type="button" className="rv-ls-fantasma" aria-label={`Preparar la liquidación de ${nombreRrpp(p)}`} onClick={(e) => { e.stopPropagation(); setPanel({ tipo: 'preparar', rrpp: p.id }); }}>
                                    <Icono nombre="wallet" />
                                </button>
                            ) : undefined,
                        }))}
                    />
                )
            ) : pagina.error !== null && pagina.datos === null ? (
                <EstadoDelError props={props} error={pagina.error} seccion="Liquidaciones" nombre="las liquidaciones" onReintentar={pagina.recargar} />
            ) : pagina.datos === null ? (
                <Cargando nombre="las liquidaciones" />
            ) : pagina.datos.data.length === 0 ? (
                <Vacia
                    icono="wallet"
                    titulo={{ draft: 'No hay borradores.', closed: 'Todavía no has cerrado ninguna liquidación.', discarded: 'No hay liquidaciones descartadas.' }[vista]}
                    texto={vista === 'draft' ? 'Prepara una desde «Por liquidar»: eliges RRPP y periodo y la revisas antes de cerrarla.' : 'Aquí quedan, con sus pagos.'}
                >
                    {vista === 'draft' && gestionar && <Boton icono="wallet" onClick={() => { setPanel({ tipo: 'preparar', rrpp: '' }); }}>Preparar liquidación</Boton>}
                </Vacia>
            ) : (
                <>
                    {/* G834: las sumas de todas las de este filtro, de la API (no solo de esta página). */}
                    {pagina.datos.totals !== undefined && (
                        <AvisoVista tono="info" icono="wallet" titulo={`Total ${dinero(pagina.datos.totals.total)}`} texto={`Pagado ${dinero(pagina.datos.totals.paid_total)}`} />
                    )}
                    <Tabla
                        etiqueta="Liquidaciones"
                        movil={movil}
                        columnas={[
                            { clave: 'rrpp', titulo: 'RRPP', ancho: 'minmax(200px, 1.4fr)' },
                            { clave: 'periodo', titulo: 'Periodo', ancho: 'minmax(130px, 1fr)' },
                            { clave: 'total', titulo: 'Total', ancho: 'minmax(110px, 0.7fr)', alinear: 'derecha' },
                            ...(vista === 'closed' ? [{ clave: 'pagos', titulo: 'Pagos', ancho: 'minmax(180px, 1.1fr)' }] : []),
                        ]}
                        filas={pagina.datos.data.map((l): FilaTabla => {
                            const nombre = nombreRrpp(mapa.get(l.promoter_id));
                            const chip = chipLiquidacion(l);
                            const queda = quedaPorPagar(l);
                            return {
                                clave: l.id,
                                etiqueta: nombre,
                                onAbrir: () => { setPanel({ tipo: 'ficha', id: l.id }); },
                                celdas: {
                                    rrpp: (
                                        <span className="rv-ls-celda-persona">
                                            <span className="rv-ca-inicial" aria-hidden="true">{iniciales(nombre)}</span>
                                            <span className="rv-ca-fila__texto">
                                                <strong>{nombre}</strong>
                                                <span>{l.status === 'closed' ? `Cerrada el ${diaHora(l.closed_at)}` : l.status === 'draft' ? 'Sin cerrar' : 'Sus comisiones volvieron a pendientes'}</span>
                                            </span>
                                        </span>
                                    ),
                                    periodo: <span className="rv-ls-texto-medio">{periodo(l)}</span>,
                                    total: <span className="rv-ls-importe"><strong>{dinero(l.total)}</strong>{(l.adjustments?.length ?? 0) > 0 && <small>con {l.adjustments?.length} ajuste{(l.adjustments?.length ?? 0) > 1 ? 's' : ''}</small>}</span>,
                                    pagos:
                                        chip !== null && chip[0] !== 'Pagada' ? (
                                            <span className="rv-ca-fila__texto"><strong><Chip estado={chip[1]}>{chip[0]}</Chip></strong><span>{l.paid_total.amount > 0 ? `Pagado ${dinero(l.paid_total)} · quedan ${dinero(queda)}` : `Quedan ${dinero(queda)}`}</span></span>
                                        ) : (
                                            <span className="rv-ca-fila__texto"><strong>{dinero(l.paid_total)}</strong><span>Todo pagado</span></span>
                                        ),
                                },
                                tarjeta: (
                                    <span className="rv-ca-fila__texto">
                                        <strong>{nombre}{chip !== null && l.status === 'closed' && chip[0] !== 'Pagada' && <Chip estado={chip[1]}>{chip[0]}</Chip>}</strong>
                                        <span>{periodo(l)} · {dinero(l.total)}</span>
                                    </span>
                                ),
                            };
                        })}
                    />
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(pagina.datos.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(pagina.datos?.next_cursor); }} movil={movil} />
                </>
            )}
            {panel?.tipo === 'preparar' && <Preparar ctx={ctx} rrppInicial={panel.rrpp} />}
            {panel?.tipo === 'ficha' && <FichaLiquidacion ctx={ctx} liquidacionId={panel.id} />}
        </div>
    );
}
